import { 
  Box, 
  Card, 
  CardContent, 
  Divider, 
  Grid, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Checkbox,
  Select, 
  MenuItem,
  Container,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Fade,
  Alert,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Zoom
} from "@mui/material";
import { 
  ArrowBack, 
  EmojiEvents, 
  CurrencyPound, 
  CardGiftcard, 
  CalendarToday, 
  Description, 
  Group, 
  Person,
  Close,
  CheckCircle,
  Assignment,
  SportsEsports,
  EmojiEventsOutlined,
  ConfirmationNumber,
  Diamond,
  Paid,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { apiJackpotService } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import { formatCash, formatPrice } from "../../utils/formatNumber";
import { IMAGE_BASE } from "../../utils/constants";
import CardLayout from "../../layouts/CardLayout";
import { fDate } from "../../utils/formatTime";
import { toast } from "react-toastify";

const WinnersJackpotManagement = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [winnersData, setWinnersData] = useState([]);
  const [participantsMap, setParticipantsMap] = useState({});
  const [pageSize] = useState(10);
  const [jackpotData, setJackpotData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const contestList = location.state?.contest;
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userPrizeMap, setUserPrizeMap] = useState({});
  const [staticUsers, setStaticUsers] = useState({});
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageTitle, setSelectedImageTitle] = useState('');

  const fetchJackpotData = async () => {
    try {
      setLoading(true);
      const response = await apiJackpotService.getById({ id });
      const jackpot = response?.data?.data;
      setData(jackpot);
      setJackpotData(jackpot);
  
      const winnersByContest = jackpot.winners?.reduce((acc, winner) => {
        const { contestId, userId } = winner;
        if (!acc[contestId]) acc[contestId] = [];
        acc[contestId].push(userId);
        return acc;
      }, {});
  
      const formattedUsers = jackpot?.fantasyUsers?.map((user) => ({
        ...user,
        id: String(user._id),
      })) || [];
      setStaticUsers(formattedUsers);
  
      // Set preselected winners and prize mappings
      const winnerUserIds = jackpot.winners?.map(w => String(w.userId)) || [];
      setSelectedUserIds(winnerUserIds);
  
      const prizeMap = {};
      jackpot.winners?.forEach(w => {
        prizeMap[String(w.userId)] = findPrizeIdByValue(jackpot, w.prize);
      });
      setUserPrizeMap(prizeMap);
  
      const updatedWinnersData = (jackpot.contestId || []).map((contestId) => ({
        contestId,
        winner: winnersByContest?.[contestId] || [],
      }));
      setWinnersData(updatedWinnersData);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };
  
  const findPrizeIdByValue = (jackpot, value) => {
    const match = [
      ...(jackpot.prizeMoney || []),
      ...(jackpot.prizeProduct || []),
      ...(jackpot.prizeGems || []),
      ...(jackpot.prizeSdCoins || []),
    ].find(
      (item) => item.prizeAmount?.toString() === value?.toString() || item.prizeName === value
    );
    return match?._id || "";
  };

  useEffect(() => {
    if (id) fetchJackpotData();
  }, [id]);

  useEffect(() => {
    if (!id && !contestList) navigate(-1);
  }, [id]);

  const totalPrizeCount =
    (data?.prizeProduct?.length || 0) +
    (data?.prizeMoney?.length || 0) +
    (data?.prizeGems?.length || 0) +
    (data?.prizeSdCoins?.length || 0);

  // Image modal functions
  const handleImageClick = (imageUrl, title) => {
    setSelectedImage(imageUrl);
    setSelectedImageTitle(title);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setSelectedImageTitle('');
  };

  // Status color helper
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { color: '#4caf50', bg: 'rgba(76, 175, 80, 0.1)' };
      case 'inactive':
        return { color: '#f44336', bg: 'rgba(244, 67, 54, 0.1)' };
      case 'ongoing':
        return { color: '#ff9800', bg: 'rgba(255, 152, 0, 0.1)' };
      case 'announced':
        return { color: '#9c27b0', bg: 'rgba(156, 39, 176, 0.1)' };
      default:
        return { color: '#757575', bg: 'rgba(117, 117, 117, 0.1)' };
    }
  };

  const statusColors = getStatusColor(data?.status);

  const allPrizes = [
    ...(data?.prizeProduct || []),
    ...(data?.prizeMoney || []),
    ...(data?.prizeGems || []),
    ...(data?.prizeSdCoins || []),
  ];

  const prizeOptions = allPrizes.map((item, index) => {
    const prizeId = item._id;
    const prizeNumber = index + 1;
    let label = "";
    let image = null;

    if (item?.prizeName) {
      label = `${prizeNumber} Prize: Product - ${item.prizeName}`;
      image = item.prizeImages?.[0] ? `${IMAGE_BASE}${item.prizeImages[0]}` : null;
    } else if (item?.prizeAmount !== undefined && item?.prizeAmount !== null) {
      if (data?.prizeType === "gems") {
        label = `${prizeNumber} Prize: ${formatPrice(item.prizeAmount)} Gems`;
      } else if (data?.prizeType === "sdCoin") {
        label = `${prizeNumber} Prize: ${formatPrice(item.prizeAmount)} SD Coins`;
      } else {
        label = `${prizeNumber} Prize: ${formatCash(item.prizeAmount)}`;
      }
    } else {
      label = `${prizeNumber} Prize`;
    }

    return {
      id: prizeId,
      label,
      image
    };
  });

  const handleSaveAssignments = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("No users selected!");
      return;
    }

    const payload = selectedUserIds.map((userId) => ({
      userId,
      prizeId: userPrizeMap[userId]
    }));

    const unassigned = payload.filter(item => !item.prizeId);
    if (unassigned.length > 0) {
      toast.error("Please assign a prize to all selected users before saving.");
      return;
    }
    try {
      setLoading(true);

      const response = await apiJackpotService.selectWinners({
        jackpotId: id,
        winners: payload
      });

      toast.success("Saved successfully!");
      navigate(-1)
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 4,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '5px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                background: 'radial-gradient(circle, rgba(0, 161, 221, 0.1) 0%, transparent 70%)',
                borderRadius: '50%',
              }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={4}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <ArrowBack sx={{ fontSize: 24 }} />
              </IconButton>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  boxShadow: '0 12px 40px rgba(0, 161, 221, 0.4)',
                  border: '3px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <EmojiEvents sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 2, lineHeight: 1.2 }}>
                  {data?.name || "Jackpot Winners Management"}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                    Jackpot #{data?.jackpotNumber || "N/A"}
                  </Typography>
                  <Box sx={{ width: '4px', height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.5)', borderRadius: '50%' }} />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarToday sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                      {fDate(data?.drawDate)}
      </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Chip
                label={capitalize(data?.status || "Unknown")}
                sx={{
                  backgroundColor: statusColors.bg,
                  color: statusColors.color,
                  fontWeight: 700,
                  fontSize: '1rem',
                  height: 40,
                  border: '2px solid',
                  borderColor: statusColors.color,
                  '&:hover': {
                    backgroundColor: statusColors.color,
                    color: 'white',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s ease'
                }}
              />
            </Stack>
          </Paper>

      {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Loader />
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 100%)',
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Avatar 
                      sx={{ 
                        backgroundColor: 'primary.main',
                        boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)',
                        width: 48,
                        height: 48
                      }}
                    >
                      <Description sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                      Basic Information
                    </Typography>
                  </Stack>
                  
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                      <Stack spacing={3}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Jackpot Name
                          </Typography>
                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                            {data?.name || "N/A"}
                          </Typography>
                        </Box>
                        
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Jackpot Number
                  </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, fontFamily: 'monospace' }}>
                            #{data?.jackpotNumber || "N/A"}
                  </Typography>
                        </Box>
                      </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                      <Stack spacing={3}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Draw Date
                  </Typography>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ backgroundColor: 'secondary.main', width: 32, height: 32 }}>
                              <CalendarToday sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                              {fDate(data?.drawDate)}
                            </Typography>
                          </Stack>
                        </Box>
                        
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: statusColors.color,
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Status
                  </Typography>
                          <Chip
                            label={capitalize(data?.status || "Unknown")}
                            sx={{
                              backgroundColor: statusColors.bg,
                              color: statusColors.color,
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              height: 32,
                              border: '2px solid',
                              borderColor: statusColors.color,
                              '&:hover': {
                                backgroundColor: statusColors.color,
                                color: 'white',
                              }
                            }}
                          />
                        </Box>
                      </Stack>
                </Grid>

                    {/* Description */}
                <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Description
                  </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.8,
                            whiteSpace: "pre-wrap",
                            fontSize: '1rem',
                            fontWeight: 400
                          }}
                        >
                          {data?.description || "No description available for this jackpot."}
                  </Typography>
                      </Box>
                </Grid>
                </Grid>
                </Paper>
              </Grid>

              {/* Prize Details */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)',
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Avatar 
                      sx={{ 
                        backgroundColor: 'warning.main',
                        boxShadow: '0 4px 16px rgba(255, 152, 0, 0.3)',
                        width: 48,
                        height: 48
                      }}
                    >
                      <CardGiftcard sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                Prize Details
                    </Typography>
                  </Stack>

                  {allPrizes.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No prizes available for this jackpot.
                    </Alert>
                  ) : (
                    <Grid container spacing={3}>
                      {/* Product Prizes */}
                      {data?.prizeProduct?.map((product, index) => (
                        <Grid item xs={12} sm={6} md={4} key={product._id || index}>
                          <Card
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              background: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderColor: 'warning.main',
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                              <Avatar sx={{ backgroundColor: 'warning.main', width: 32, height: 32 }}>
                                <CardGiftcard sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                Product Prize #{index + 1}
                              </Typography>
                            </Stack>
                            
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                              {product.prizeName || "Unnamed Product"}
              </Typography>

                            {product.prizeImages?.length > 0 ? (
                              <Grid container spacing={1}>
                                {product.prizeImages.slice(0, 4).map((img, imgIndex) => (
                                  <Grid item xs={6} key={imgIndex}>
                                      <Box
                                        component="img"
                                        src={`${IMAGE_BASE}${img}`}
                                      alt={`Product ${index + 1} - Image ${imgIndex + 1}`}
                                      onClick={() => handleImageClick(`${IMAGE_BASE}${img}`, product.prizeName || `Product ${index + 1}`)}
                                        sx={{
                                          width: "100%",
                                        height: 80,
                                          objectFit: "cover",
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'scale(1.05)',
                                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                                        }
                                        }}
                                      />
                                    </Grid>
                                ))}
                                {product.prizeImages.length > 4 && (
                                  <Grid item xs={6}>
                                    <Box
                                      sx={{
                                        height: 80,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: 2,
                                        border: '1px dashed',
                                        borderColor: 'divider'
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        +{product.prizeImages.length - 4} more
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            ) : (
                              <Box
                                sx={{
                                  height: 80,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: 2,
                                  border: '1px dashed',
                                  borderColor: 'divider'
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  No Images
                                </Typography>
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      ))}

                      {/* Money Prizes */}
                      {data?.prizeMoney?.map((money, index) => (
                        <Grid item xs={12} sm={6} md={4} key={money._id || index}>
                          <Card
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              background: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderColor: 'success.main',
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                              <Avatar sx={{ backgroundColor: 'success.main', width: 32, height: 32 }}>
                                <CurrencyPound sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                Money Prize #{index + 1}
                              </Typography>
                            </Stack>
                            
                            <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700 }}>
                              {formatCash(money.prizeAmount || 0)}
                            </Typography>
                          </Card>
                        </Grid>
                      ))}

                      {data?.prizeGems?.map((gem, index) => (
                        <Grid item xs={12} sm={6} md={4} key={gem._id || index}>
                          <Card
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              background: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderColor: '#9c27b0',
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                              <Avatar sx={{ backgroundColor: '#9c27b0', width: 32, height: 32 }}>
                                <Diamond sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                Gem Prize #{index + 1}
                              </Typography>
                            </Stack>

                            <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 700 }}>
                              {formatPrice(gem.prizeAmount || 0)} Gems
                            </Typography>
                          </Card>
                        </Grid>
                      ))}

                      {data?.prizeSdCoins?.map((coin, index) => (
                        <Grid item xs={12} sm={6} md={4} key={coin._id || index}>
                          <Card
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              background: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderColor: '#ffc107',
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                              <Avatar sx={{ backgroundColor: '#ff8f00', width: 32, height: 32 }}>
                                <Paid sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                SD Coin Prize #{index + 1}
                              </Typography>
                            </Stack>

                            <Typography variant="h4" sx={{ color: '#ffc107', fontWeight: 700 }}>
                              {formatPrice(coin.prizeAmount || 0)} SD Coins
                            </Typography>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Grid>

              {/* Associated Contests */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #9c27b0 0%, #e1bee7 100%)',
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Avatar 
                      sx={{ 
                        backgroundColor: 'secondary.main',
                        boxShadow: '0 4px 16px rgba(156, 39, 176, 0.3)',
                        width: 48,
                        height: 48
                      }}
                    >
                      <Group sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                      Associated Contests
              </Typography>
                  </Stack>

              {data?.contestDetails?.length > 0 ? (
                <Grid container spacing={2}>
                  {data.contestDetails.map((contest, index) => (
                    <Grid item xs={12} sm={6} md={4} key={contest._id}>
                          <Card
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              background: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderColor: 'secondary.main',
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{ backgroundColor: 'secondary.main', width: 32, height: 32 }}>
                                <Group sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                {contest.contestName || "Unnamed Contest"}
                        </Typography>
                            </Stack>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No contests associated with this jackpot.
                    </Alert>
                  )}
                </Paper>
              </Grid>

              {/* Winner Assignment */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #4caf50 0%, #81c784 100%)',
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Avatar 
                      sx={{ 
                        backgroundColor: 'success.main',
                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                        width: 48,
                        height: 48
                      }}
                    >
                      <Assignment sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                        Winner Assignment
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select winners and assign prizes (Max: {totalPrizeCount} winners)
              </Typography>
                    </Box>
                    <Badge 
                      badgeContent={selectedUserIds.length} 
                      color="primary"
                      sx={{ ml: 'auto' }}
                    >
                      <Chip 
                        label={`${selectedUserIds.length}/${totalPrizeCount} Selected`}
                        color="primary"
                        variant="outlined"
                      />
                    </Badge>
                  </Stack>

                  <TableContainer 
                    component={Paper}
                    elevation={0}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                    }}
                  >
                <Table>
                  <TableHead>
                    <TableRow>
                          <TableCell padding="checkbox" sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              SELECT
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              USER NAME
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              CONTESTS
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              GAMES JOINED
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              TOTAL JACKPOTS JOINED
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              JACKPOTS WON
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                            JACKPOT ENTRIES
                            </Typography>
                          </TableCell>
                          
                          <TableCell sx={{ borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              ASSIGNED PRIZE
                            </Typography>
                          </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staticUsers.map((user) => {
                      const isChecked = selectedUserIds.includes(user.id);
                      return (
                            <TableRow 
                              key={user.id}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                }
                              }}
                            >
                              <TableCell padding="checkbox" sx={{ borderColor: 'divider' }}>
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (checked) {
                                  if (selectedUserIds.length < totalPrizeCount) {
                                    setSelectedUserIds([...selectedUserIds, user.id]);
                                  } else {
                                    toast.error(`Cannot select more than ${totalPrizeCount} users`);
                                  }
                                } else {
                                  setSelectedUserIds(selectedUserIds.filter((id) => id !== user.id));
                                  setUserPrizeMap((prev) => {
                                    const updated = { ...prev };
                                    delete updated[user.id];
                                    return updated;
                                  });
                                }
                              }}
                                  sx={{
                                    color: 'primary.main',
                                    '&.Mui-checked': {
                                      color: 'primary.main',
                                }
                              }}
                            />
                          </TableCell>
                              <TableCell sx={{ borderColor: 'divider' }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Avatar sx={{ width: 32, height: 32, backgroundColor: 'primary.main' }}>
                                    <Person sx={{ fontSize: 16 }} />
                                  </Avatar>
                                  <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                                    {user?.fullname || "Unknown User"}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {user?.contestNames?.join(", ") || "No contests"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderColor: 'divider' }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ backgroundColor: 'info.main', width: 28, height: 28 }}>
                                    <SportsEsports sx={{ fontSize: 16, color: 'white' }} />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                      {user?.totalGamesJoined || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      Games
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ borderColor: 'divider' }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ backgroundColor: 'success.main', width: 28, height: 28 }}>
                                    <CardGiftcard sx={{ fontSize: 16, color: 'white' }} />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                      {user?.totalContestJackpotsJoined || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      Contest Jackpots
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ borderColor: 'divider' }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ backgroundColor: 'warning.main', width: 28, height: 28 }}>
                                    <EmojiEventsOutlined sx={{ fontSize: 16, color: 'white' }} />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                      {user?.totalJackpotsWon || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      Jackpots
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>

                              <TableCell sx={{ borderColor: 'divider' }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ backgroundColor: 'warning.main', width: 28, height: 28 }}>
                                    <ConfirmationNumber sx={{ fontSize: 16, color: 'white' }} />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                      {user?.jackpotEntries || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                     Jackpot Entries
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>

                              
                              <TableCell sx={{ borderColor: 'divider' }}>
                            {isChecked ? (
                              <Select
                                value={userPrizeMap[user.id] || ""}
                                onChange={(e) => {
                                  const prizeId = e.target.value;
                                  setUserPrizeMap((prev) => ({
                                    ...prev,
                                    [user.id]: prizeId
                                  }));
                                }}
                                displayEmpty
                                size="small"
                                    sx={{ 
                                      minWidth: 250,
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'divider',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                      }
                                    }}
                                  >
                                    <MenuItem value="">
                                      <Typography variant="body2" color="text.secondary">
                                        Select Prize
                                      </Typography>
                                    </MenuItem>
                                {prizeOptions
                                  .filter(
                                    (prize) =>
                                      !Object.entries(userPrizeMap)
                                        .filter(([otherUserId]) => otherUserId !== user.id)
                                        .some(([, assignedPrizeId]) => assignedPrizeId === prize.id)
                                  )
                                  .map((prize) => (
                                    <MenuItem key={prize.id} value={prize.id}>
                                          <Stack direction="row" alignItems="center" spacing={2}>
                                        {prize.image && (
                                            <Box
                                              component="img"
                                              src={prize.image}
                                              alt={prize.label}
                                              sx={{
                                                  width: 32,
                                                  height: 32,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                              }}
                                            />
                                        )}
                                            <Typography variant="body2" sx={{ color: 'white' }}>
                                          {prize.label}
                                            </Typography>
                                          </Stack>
                                    </MenuItem>
                                  ))}
                              </Select>
                            ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    Not selected
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                      size="large"
                      startIcon={<CheckCircle />}
                  onClick={handleSaveAssignments}
                  disabled={selectedUserIds.length === 0}
                      sx={{
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                          boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'text.disabled',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Save Winner Assignments
                </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Image View Modal */}
          <Dialog
            open={imageModalOpen}
            onClose={handleCloseImageModal}
            maxWidth="md"
            fullWidth
            TransitionComponent={Zoom}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                borderRadius: 3,
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                {selectedImageTitle}
              </Typography>
              <IconButton
                onClick={handleCloseImageModal}
                sx={{ color: 'white' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {selectedImage && (
                <Box
                  component="img"
                  src={selectedImage}
                  alt={selectedImageTitle}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: 2
                  }}
                />
              )}
            </DialogContent>
          </Dialog>
        </Stack>
      </Fade>
    </Container>
  );
};

export default WinnersJackpotManagement;